import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import cx from "classnames";
import { forwardRef } from "react";
import cn from "variant-classnames";
import { toolkitCx, toolkitTestIds } from "../../../constants";
import ConditionalWrapper from "../ConditionalWrapper";
const getButtonVariants = (isActive, outlined) => ({
    $all: cx("flex gap-2 items-center justify-center disabled:cursor-not-allowed disabled:opacity-60 group", toolkitCx.layout.transition.default, toolkitCx.layout.border.weight.default, toolkitCx.recipes.scaleWhenActive),
    loading: "motion-safe:animate-pulse",
    variant: {
        primary: cx({
            "bg-primary-200": isActive && !outlined,
            "bg-primary-200/10": isActive && outlined,
            "bg-primary hover:bg-primary-200 text-white": !isActive && !outlined,
            [`border-primary ${toolkitCx.layout.text.primary} hover:bg-primary-200/20`]: outlined,
            "border-transparent": !outlined,
        }),
        secondary: cx({
            "hover:bg-gray-400/50 hover:dark:bg-primary/25": !outlined,
            "hover:bg-gray-400/50 hover:dark:bg-gray-700": outlined,
            "bg-gray-400/75 dark:bg-gray-700": isActive && !outlined,
            "bg-gray-400 dark:bg-primary/20 text-gray-800 dark:text-gray": !isActive && !outlined,
            [`${toolkitCx.layout.border.silent} ${toolkitCx.layout.text.default}`]: outlined,
            "border-transparent": !outlined,
        }),
        neutral: cx("hover:bg-gray-400 hover:dark:bg-gray-600/75", {
            "hover:bg-gray-400 hover:dark:bg-gray-600/75": !outlined,
            "hover:bg-gray-300 hover:dark:bg-gray-600/75": outlined,
            "bg-gray-400 dark:bg-gray-600/60": isActive && !outlined,
            "bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray": !isActive && !outlined,
            [`border-gray-800/20 dark:border-gray/20 ${toolkitCx.layout.text.muted}`]: outlined,
            "border-transparent": !outlined,
        }),
        success: cx({
            "bg-success-200": isActive && !outlined,
            "bg-success-200/10": isActive && outlined,
            "bg-success hover:bg-success-200 text-white": !isActive && !outlined,
            [`border-success ${toolkitCx.layout.text.success} hover:bg-success-200/20`]: outlined,
            "border-transparent": !outlined,
        }),
        error: cx({
            "bg-error-200": isActive && !outlined,
            "bg-error hover:bg-error-200 text-white": !isActive && !outlined,
            [`border-error ${toolkitCx.layout.text.error} hover:bg-error-200/20`]: outlined,
            "bg-error-200/10": isActive && outlined,
            "border-transparent text-white": !outlined,
        }),
        warning: cx({
            "bg-warning-300/70": isActive && !outlined,
            "bg-warning-300/10": isActive && outlined,
            "bg-warning-300 hover:bg-warning-300/80 text-white": !isActive && !outlined,
            [`border-warning-300 ${toolkitCx.layout.text.warning} hover:bg-warning-300/20`]: outlined,
            "border-transparent": !outlined,
        }),
        gradient: cx("relative inline-flex justify-center items-center overflow-hidden group border-none", { "text-white": !outlined }, { [`${toolkitCx.layout.text.default}`]: outlined }, toolkitCx.layout.background.gradient, toolkitCx.layout.background.gradientHoverable),
        /**
         * @deprecated
         * All definitions below are deprecated. See type definitions for details.
         */
        info: cx("text-white hover:bg-primary-200", {
            "bg-primary-200": isActive,
            "bg-primary": !isActive,
        }),
        colored: cx("text-white", toolkitCx.layout.background.gradient, toolkitCx.layout.background.gradientHoverable),
        "secondary-outlined": cx("text-black dark:text-gray-200 hover:bg-gray-200 hover:dark:bg-gray-700 border border-primary/60", {
            "bg-gray-200 dark:bg-gray-800": isActive,
            "bg-gray-200/40 dark:bg-gray-800/40": !isActive,
        }),
        "warning-transparent": cx("text-warning-300 hover:bg-warning-300/10 dark:hover:bg-warning-200/20 border border-warning-300", {
            "bg-warning-300/10 dark:bg-warning-200/20": isActive,
            "bg-warning-200/10 dark:bg-warning-300/20": !isActive,
        }),
        "warning-outlined": cx("text-warning-300 hover:bg-warning-300/10 dark:hover:bg-warning-200/20 border border-warning-300", {
            "bg-warning-300/10 dark:bg-warning-200/20": isActive,
            "bg-warning-200/10 dark:bg-warning-300/20": !isActive,
        }),
    },
    size: {
        xs: cx("min-w-[100px] px-3", toolkitCx.layout.textSize.xs, toolkitCx.layout.sizes.height.xs),
        sm: cx("min-w-[100px] px-3", toolkitCx.layout.textSize.sm, toolkitCx.layout.sizes.height.sm),
        md: cx("min-w-[125px] px-5", toolkitCx.layout.textSize.base, toolkitCx.layout.sizes.height.md),
        lg: cx("min-w-[125px] px-5", toolkitCx.layout.textSize.lg, toolkitCx.layout.sizes.height.lg),
        xl: cx("min-w-[150px] px-6", toolkitCx.layout.textSize.xl, toolkitCx.layout.sizes.height.xl),
    },
    rounded: {
        sm: "rounded-sm",
        md: "rounded-md",
        lg: "rounded-lg",
        xl: "rounded-xl",
        "2xl": "rounded-2xl",
        "3xl": "rounded-3xl",
        full: "rounded-full",
    },
});
/**
 * The Button component is a versatile, customizable button with various styling options,
 * such as size, shape (rounded corners), and variant (color scheme). It also supports
 * a loading state, which displays a loader within the button.
 *
 * @component
 * @param {IButtonProps} props - The props for the Button component.
 * @param {("sm"|"lg"|"custom")} [props.size] - The size of the button. One of "sm", "lg", "custom". Default is "lg".
 * @param {boolean} [props.isActive] - If true, renders the active state layout.
 * @param {boolean} [props.isGradientSplitButton] - Flag that should not be set. It is set automatically in the `<SplitButton />` component to disable the border radius on the right side of the primary button.
 * @param {("sm"|"md"|"lg"|"xl"|"2xl"|"3xl")} [props.rounded] - The degree of rounded corners for the button. One of "sm", "md", "lg", "xl", "2xl", "3xl". Default is "lg".
 * @param {("primary"|"secondary"|"secondary-outlined"|"colored"|"silent"|"error"|"info"|"warning"|"warning-transparent"|"warning-transparent"|"success")} [props.variant] - The color scheme of the button. One of "primary", "secondary", "secondary-outlined", "colored", "silent", "error", "info", "warning", "warning-transparent", "success". Default is "primary".
 * @param {React.Ref<HTMLButtonElement>} forwardedRef - A ref that can be used to access the underlying HTMLButtonElement of the Button component.
 * @returns {JSX.Element} A rendered Button component.
 */
const Button = ({ children, className, isActive, isGradientSplitButton, outlined, rounded = "lg", size = "md", testId = toolkitTestIds.button, variant = "primary", ...props }, forwardedRef) => {
    const classes = cn(getButtonVariants(isActive, outlined), {
        className,
        rounded,
        outlined,
        size,
        variant,
    });
    return (_jsx("button", { className: classes, "data-testid": testId, ref: forwardedRef, ...props, children: _jsx(ConditionalWrapper, { condition: !!outlined && variant === "gradient", wrapper: (children) => {
                return (_jsxs(_Fragment, { children: [_jsx("span", { className: cx("absolute z-10 h-[calc(100%-2px)] w-[calc(100%-2px)] bg-gray group-hover:opacity-80 dark:bg-gray-800", {
                                "rounded-sm": rounded === "sm",
                                "rounded-md": rounded === "md",
                                "rounded-lg": rounded === "lg",
                                "rounded-xl": rounded === "xl",
                                "rounded-2xl": rounded === "2xl",
                                "rounded-3xl": rounded === "3xl",
                                "rounded-full": rounded === "full",
                                "!rounded-br-none !rounded-tr-none": isGradientSplitButton,
                            }, toolkitCx.layout.transition.opacity, toolkitCx.recipes.center.both()) }), _jsx("span", { className: "relative z-20", children: children })] }));
            }, children: children }) }));
};
export default forwardRef(Button);
//# sourceMappingURL=Button.js.map